import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { getAnalyticsSummary, sendSalesDigest } from "../lib/api";
import {
  DollarSign, ShoppingBag, BarChart3, AlertTriangle, TrendingUp, Package,
  CalendarDays, Send, ChevronRight,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import ProductTrendDrawer from "../components/ProductTrendDrawer";

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat("en-US").format(n || 0);
const toISO = (d) => (d ? format(d, "yyyy-MM-dd") : "");

function Sparkline({ data }) {
  const W = 720;
  const H = 140;
  const pad = 8;
  const max = Math.max(1, ...data.map((d) => d.revenue));
  const stepX = (W - pad * 2) / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = pad + i * stepX;
    const y = H - pad - (d.revenue / max) * (H - pad * 2);
    return [x, y];
  });
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const area = `${path} L ${(pad + (data.length - 1) * stepX).toFixed(1)} ${H - pad} L ${pad} ${H - pad} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[140px]" data-testid="analytics-sparkline">
      <defs>
        <linearGradient id="sparkfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#002FA7" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#002FA7" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#sparkfill)" />
      <path d={path} fill="none" stroke="#002FA7" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={data[i].revenue > 0 ? 3 : 1.5} fill="#002FA7" />
      ))}
    </svg>
  );
}

function Kpi({ label, value, sub, Icon, testid }) {
  return (
    <div className="border border-zinc-200 p-6 bg-white relative overflow-hidden" data-testid={testid}>
      <div className="flex items-start justify-between mb-6">
        <div className="micro-label">{label}</div>
        <div className="w-8 h-8 klein-bg flex items-center justify-center">
          <Icon size={16} strokeWidth={2.5} color="#ffffff" />
        </div>
      </div>
      <div className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>
        {value}
      </div>
      {sub && <div className="micro-label mt-2 text-zinc-500">{sub}</div>}
    </div>
  );
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [range, setRange] = useState("14d");
  const [customStart, setCustomStart] = useState(null); // Date | null
  const [customEnd, setCustomEnd] = useState(null);
  const [drawerProduct, setDrawerProduct] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    setErr(null);
    const params = { range };
    if (range === "custom") {
      if (!customStart || !customEnd) return; // wait for both
      params.start = toISO(customStart);
      params.end = toISO(customEnd);
    }
    getAnalyticsSummary(params)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setErr(e?.response?.data?.detail || e?.message || "Failed to load analytics"));
    return () => {
      alive = false;
    };
  }, [range, customStart, customEnd]);

  const trendDelta = useMemo(() => {
    if (!data?.trend?.length) return 0;
    const half = Math.floor(data.trend.length / 2);
    const first = data.trend.slice(0, half).reduce((a, b) => a + b.revenue, 0);
    const second = data.trend.slice(half).reduce((a, b) => a + b.revenue, 0);
    if (first === 0) return second > 0 ? 100 : 0;
    return Math.round(((second - first) / first) * 100);
  }, [data]);

  const RANGES = [
    { key: "today", label: "Today" },
    { key: "7d", label: "7 days" },
    { key: "14d", label: "14 days" },
    { key: "30d", label: "30 days" },
    { key: "custom", label: "Custom" },
  ];

  const halfLabel = useMemo(() => {
    if (!data?.trend?.length) return "prior period";
    const days = data.trend.length;
    if (days <= 1) return "prior day";
    const halfDays = Math.floor(days / 2);
    return `prior ${halfDays}d`;
  }, [data]);

  const handleSendDigest = async () => {
    const ranges = ["today", "7d", "14d", "30d"];
    const r = ranges.includes(range) ? range : "7d";
    setSendingDigest(true);
    try {
      const res = await sendSalesDigest(r);
      toast.success("Sales digest sent", { description: res?.message || `Window: ${r}` });
    } catch (e) {
      toast.error("Failed to send sales digest", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setSendingDigest(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="analytics-page">
      <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="micro-label mb-3">PERFORMANCE</div>
          <h1 className="text-5xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>
            Sales Analytics
          </h1>
          <p className="text-zinc-600 mt-2 max-w-xl">
            Live snapshot of paid orders pulled from Stripe Checkout — revenue, regional split, and best-sellers.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleSendDigest}
            disabled={sendingDigest || !data}
            className="text-xs uppercase tracking-wider font-bold px-3 h-10 border border-zinc-300 hover:bg-zinc-50 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="send-sales-digest"
            title="Email the current window's summary via Resend"
          >
            <Send size={14} strokeWidth={2.5} className={sendingDigest ? "animate-pulse" : ""} />
            {sendingDigest ? "Sending..." : "Email this digest"}
          </button>
          <div className="micro-label text-zinc-500" data-testid="analytics-window-label">
            {data ? `${data.start} → ${data.end}` : "14-DAY WINDOW"} · USD
          </div>
        </div>
      </div>

      {/* Range selector */}
      <div className="border border-zinc-200 mb-0 -mt-2 flex items-center flex-wrap gap-0 bg-white" data-testid="range-selector">
        {RANGES.map((r) => (
          <button
            key={r.key}
            data-testid={`range-${r.key}`}
            onClick={() => setRange(r.key)}
            className={`px-4 h-11 text-[11px] uppercase tracking-wider font-bold border-r border-zinc-200 transition-colors ${
              range === r.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"
            }`}
          >
            {r.label}
          </button>
        ))}
        {range === "custom" && (
          <div className="flex items-center gap-2 px-4 py-2 ml-auto" data-testid="custom-range-inputs">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="border border-zinc-300 px-3 h-9 text-xs inline-flex items-center gap-2 hover:bg-zinc-50 min-w-[140px] justify-between"
                  data-testid="custom-start"
                >
                  <CalendarDays size={12} strokeWidth={2.5} />
                  {customStart ? format(customStart, "MMM d, yyyy") : "Start date"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={customStart}
                  onSelect={setCustomStart}
                  disabled={(d) => (customEnd ? d > customEnd : false) || d > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <span className="text-zinc-400 text-xs">→</span>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="border border-zinc-300 px-3 h-9 text-xs inline-flex items-center gap-2 hover:bg-zinc-50 min-w-[140px] justify-between"
                  data-testid="custom-end"
                >
                  <CalendarDays size={12} strokeWidth={2.5} />
                  {customEnd ? format(customEnd, "MMM d, yyyy") : "End date"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={customEnd}
                  onSelect={setCustomEnd}
                  disabled={(d) => (customStart ? d < customStart : false) || d > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {err && (
        <div className="border border-red-200 bg-red-50 p-6 text-red-700 text-sm" data-testid="analytics-error">
          {err}
        </div>
      )}

      {!data && !err && (
        <div className="micro-label" data-testid="analytics-loading">
          LOADING ANALYTICS...
        </div>
      )}

      {data && (
        <>
          {/* KPI Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 -mt-px" data-testid="kpi-grid">
            <Kpi
              label="TOTAL REVENUE"
              value={fmtUSD(data.total_revenue)}
              sub={`${trendDelta >= 0 ? "+" : ""}${trendDelta}% vs. ${halfLabel}`}
              Icon={DollarSign}
              testid="kpi-revenue"
            />
            <Kpi
              label="ORDERS PAID"
              value={fmtNum(data.total_orders)}
              sub="Stripe payment_status=paid"
              Icon={ShoppingBag}
              testid="kpi-orders"
            />
            <Kpi
              label="AVG ORDER VALUE"
              value={fmtUSD(data.aov)}
              sub="Revenue / orders"
              Icon={BarChart3}
              testid="kpi-aov"
            />
            <Kpi
              label="SOLD-OUT SKUs"
              value={fmtNum(data.sold_out_skus)}
              sub="Any region at 0"
              Icon={AlertTriangle}
              testid="kpi-soldout"
            />
          </div>

          {/* Trend */}
          <div className="border border-zinc-200 border-t-0 p-6 md:p-8 bg-white" data-testid="trend-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="micro-label">REVENUE TREND</div>
                <div className="text-xl font-black tracking-tight mt-1" style={{ fontFamily: "Outfit" }}>
                  {fmtUSD(data.trend.reduce((a, b) => a + b.revenue, 0))} captured
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-700">
                <TrendingUp size={14} strokeWidth={2.5} />
                {trendDelta >= 0 ? "+" : ""}
                {trendDelta}%
              </div>
            </div>
            <Sparkline data={data.trend} />
            <div
              className="grid gap-1 mt-3"
              style={{ gridTemplateColumns: `repeat(${data.trend.length}, minmax(0, 1fr))` }}
            >
              {data.trend.map((d) => (
                <div key={d.date} className="text-[9px] text-zinc-400 text-center tabular-nums">
                  {d.date.slice(5)}
                </div>
              ))}
            </div>
          </div>

          {/* Region + Top products */}
          <div className="grid md:grid-cols-12 gap-0 border border-zinc-200 border-t-0">
            {/* Region breakdown */}
            <div className="md:col-span-7 p-6 md:p-8 border-r border-zinc-200" data-testid="region-breakdown">
              <div className="micro-label mb-5">REVENUE BY REGION</div>
              <div className="space-y-4">
                {data.region_breakdown.map((r) => (
                  <div key={r.code} data-testid={`region-row-${r.code}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] uppercase tracking-wider font-bold bg-zinc-900 text-white px-2 py-0.5">
                          {r.code}
                        </span>
                        <span className="font-bold text-sm tracking-tight">{r.label}</span>
                      </div>
                      <div className="flex items-center gap-4 tabular-nums">
                        <span className="text-xs text-zinc-500">{r.units} units</span>
                        <span className="font-black" style={{ fontFamily: "Outfit" }}>
                          {fmtUSD(r.revenue)}
                        </span>
                        <span className="text-xs text-zinc-500 w-12 text-right">{r.share}%</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-zinc-100 overflow-hidden">
                      <div
                        className="h-full klein-bg transition-all duration-500"
                        style={{ width: `${Math.min(100, r.share)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top products */}
            <div className="md:col-span-5 p-6 md:p-8" data-testid="top-products">
              <div className="micro-label mb-5">TOP PRODUCTS</div>
              {data.top_products.length === 0 ? (
                <div className="text-center py-10 text-zinc-400">
                  <Package size={36} className="mx-auto mb-3" strokeWidth={2} />
                  <p className="text-sm">No paid orders yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data.top_products.map((p, idx) => (
                    <button
                      key={p.product_id}
                      onClick={() => setDrawerProduct(p)}
                      className="w-full flex items-center gap-4 py-2 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors text-left group"
                      data-testid={`top-product-${idx}`}
                    >
                      <div className="text-2xl font-black text-zinc-300 tabular-nums w-8" style={{ fontFamily: "Outfit" }}>
                        {String(idx + 1).padStart(2, "0")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm tracking-tight truncate">{p.name}</div>
                        <div className="micro-label mt-0.5 text-zinc-500">{p.units} units sold · click to drill down</div>
                      </div>
                      <div className="font-black tabular-nums" style={{ fontFamily: "Outfit" }}>
                        {fmtUSD(p.revenue)}
                      </div>
                      <ChevronRight size={16} strokeWidth={2.5} className="text-zinc-300 group-hover:text-zinc-900 transition-colors" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {drawerProduct && (
        <ProductTrendDrawer
          product={drawerProduct}
          range={range}
          start={range === "custom" && customStart ? toISO(customStart) : null}
          end={range === "custom" && customEnd ? toISO(customEnd) : null}
          onClose={() => setDrawerProduct(null)}
        />
      )}
    </div>
  );
}
